import { GridColDef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";

import { useMonthlyGeneration } from "./useMonthlyGeneration";

const GENERATION_COLUMN_HEADER = "Generation (containers)";

/** Generation-driven kg for one month, keyed by month in a lookup. */
type GenerationCell = { hazardousKg: number; acuteKg: number; incompleteFillCount: number };

/**
 * Month key ("YYYY-MM") shared by statusByMonth rows (whose `month` is a
 * first-of-month date string) and monthlyGeneration entries, so the generation
 * column can join to a status row by month.
 */
function monthKey(monthIso: string): string {
  return monthIso.slice(0, 7);
}

/**
 * The generation-column cell text: total federal generation in kg (acute noted
 * separately), or "Incomplete" for a month whose only qualifying ADDs had
 * unknown mass (volume-only), so it doesn't read as a confident 0 kg.
 */
function formatGenerationKg(cell: GenerationCell | undefined): string {
  if (!cell) return "—";
  const totalKg = cell.hazardousKg + cell.acuteKg;
  if (totalKg <= 0) {
    return cell.incompleteFillCount > 0 ? "Incomplete" : "—";
  }
  const base = `${Math.round(totalKg).toLocaleString()} kg`;
  return cell.acuteKg > 0
    ? `${base} (${Math.round(cell.acuteKg).toLocaleString()} kg acute)`
    : base;
}

/** The year whose generation the comparison grids cover, from the tile's date filters. */
function generationYearOf(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): number | null {
  if (endDate) return new Date(endDate).getUTCFullYear();
  if (startDate) return new Date(startDate).getUTCFullYear();
  return null;
}

/**
 * The generator status tile's optional "Generation (containers)" column: the
 * month's container-derived mass in kg, shown beside the EPA-vs-calculated
 * comparison rather than as a competing tier, so the two status columns stay a
 * like-for-like comparison.
 *
 * `columns` is empty unless the tenant tracks containers, so a caller can spread
 * it unconditionally.
 */
export function useGenerationColumn({
  facilityId,
  startDate,
  endDate,
  containerTrackingEnabled,
}: {
  facilityId: string | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  containerTrackingEnabled: boolean;
}) {
  const { data } = useMonthlyGeneration(
    facilityId,
    generationYearOf(startDate, endDate),
    containerTrackingEnabled
  );

  const byMonth = useMemo(() => {
    const map = new Map<string, GenerationCell>();
    for (const entry of data?.facility?.monthlyGeneration ?? []) {
      map.set(monthKey(String(entry.month)), {
        hazardousKg: entry.hazardousKg,
        acuteKg: entry.acuteKg,
        incompleteFillCount: entry.incompleteFillCount,
      });
    }
    return map;
  }, [data]);

  /** Spread after the comparison columns; empty when the tenant isn't tracking containers. */
  const columns: GridColDef[] = containerTrackingEnabled
    ? [{ field: "generationKg", headerName: GENERATION_COLUMN_HEADER, flex: 1 }]
    : [];

  return {
    columns,
    /** Joins each month's generation kg onto the status rows the grid renders. */
    withGenerationKg: <T extends { month: string }>(rows: readonly T[]) =>
      rows.map((row) => ({
        ...row,
        generationKg: formatGenerationKg(byMonth.get(monthKey(row.month))),
      })),
    /** A pure container-tracking facility has generation months but no shipments. */
    hasGenerationData: byMonth.size > 0,
  };
}
